import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { Info } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Chart } from '@/components/chart';
import { TableCell } from '@/components/ui/table';
import { useSessionFilter } from '@/lib/dashboard-filters';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useCashflowForecast, useCashflowWidgets } from './api';
import { Board } from './board';
import { CASHFLOW_CATALOG } from './catalog';
import { HomeTabs } from './home-tabs';
import { EmptyLine, KpiValue, RankedBars, WidgetTable, overdueTone } from './widgets';

/**
 * DSH-04 — the Cash Flow dashboard + the §5e foresight chart. Cash basis
 * everywhere (BR-DSH-04-01); the KPI values are data-coloured with no change
 * pill (BR-DSH-04-07/-08); the forecast line and its projected-end figure
 * share one colour, red the moment any of the 31 points dips below zero
 * (BR-DSH-04-17/-18); the week list shows both directions with amounts
 * coloured by direction (BR-DSH-04-26); overdue rows colour the DAYS figure
 * by lateness, never the amount (BR-DSH-04-25).
 */
export function CashflowPage() {
  const { t } = useTranslation();
  /**
   * BR-DSH-04-32 / §9: the filter starts at CONSOLIDATED, which on the wire is
   * the absence of the parameter rather than a value meaning "all".
   *
   * BR-DSH-00-16: the choice persists for the browser session and resets to
   * consolidated on the next sign-in. The accept gate is the contract's own
   * `/^[A-Z]{3}$/` (plus ''), not membership of `data.currencies` — the list
   * arrives after mount. A code the company no longer holds restores as
   * ERR-DSH-04-03's zeros-and-empty state, whose own remedy column already
   * says what to do: switch back to Consolidated.
   */
  const [currency, setCurrency] = useSessionFilter<string>(
    'cashflow.currency',
    '',
    (value): value is string => typeof value === 'string' && (value === '' || /^[A-Z]{3}$/.test(value)),
  );
  const query = currency === '' ? {} : { currency };
  const { data, isError, refetch } = useCashflowWidgets(query);
  const forecast = useCashflowForecast(query);

  /**
   * WHAT THE FIGURES ARE IN, from the server rather than from a literal. The
   * screen printed `currency="USD"` beside thirteen figures, which was wrong
   * for every company that does not report in dollars — and wrong even for one
   * that does, while the server was not converting at all. Consolidated it is
   * the company's base (BR-DSH-04-10); filtered it is the chosen currency,
   * because BR-DSH-04-32 converts nothing.
   */
  const sessionBase = useBaseCurrency();
  const stated = currency === '' ? (data?.baseCurrency ?? sessionBase) : currency;

  useEffect(() => {
    document.title = pageTitle(t('dash.tabCashflow'));
  }, [t]);

  const forecastOption = useMemo<EChartsOption>(() => {
    const points = forecast.data?.points ?? [];
    const color = forecast.data?.belowZero ? '#dc2626' : '#059669';
    return {
      grid: { left: 8, right: 8, top: 8, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: points.map((p) => p.date) },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'line',
          data: points.map((p) => Number(p.balance)),
          lineStyle: { color, width: 1.4 },
          itemStyle: { color },
          areaStyle: { color, opacity: 0.1 },
          symbol: 'none',
        },
      ],
    };
  }, [forecast.data]);

  /**
   * BR-DSH-04-19: inflow GREEN, outflow NEUTRAL GREY, the net line BLUE —
   * "and the net line colour must not change with the data". All three are
   * literal constants below; none of them reads a bucket, which is what makes
   * the last clause true rather than merely true today.
   *
   * BR-DSH-04-20: "the net line must not capture the mouse, so hovering it
   * still shows the bar tooltip beneath it" — `silent: true` on the line
   * series. That is exactly ECharts' term for a series that takes no mouse
   * events, so the axis tooltip resolves to the bars underneath.
   */
  const trendOption = useMemo<EChartsOption>(() => {
    const buckets = data?.trend.buckets ?? [];
    return {
      grid: { left: 8, right: 8, top: 8, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: buckets.map((b) => b.label) },
      yAxis: { type: 'value' },
      series: [
        { name: t('dash.inflow'), type: 'bar', data: buckets.map((b) => Number(b.inflow)), itemStyle: { color: '#10b981' } },
        { name: t('dash.outflow'), type: 'bar', data: buckets.map((b) => Number(b.outflow)), itemStyle: { color: '#9ca3af' } },
        {
          name: t('dash.net'),
          type: 'line',
          data: buckets.map((b) => Number(b.net)),
          lineStyle: { color: '#2563eb', width: 1.6 },
          itemStyle: { color: '#2563eb' },
          symbol: 'none',
          silent: true,
        },
      ],
    };
  }, [data?.trend.buckets, t]);

  /**
   * BR-DSH-04-23: "Amounts in Upcoming Receivables must ALWAYS be green and
   * amounts in Upcoming Payables must ALWAYS be red, regardless of the data."
   * `tone` is a parameter fixed by the CALL SITE — `'pos'` for receivables,
   * `'neg'` for payables — and no row field can reach it. The spec's reason
   * (§5k) is that the colour states the direction of the obligation, not the
   * sign of the number, so a row must not recolour itself.
   */
  const dueTable = (
    rows: { documentId: string; party: string; documentNo: string; amount: string; due: string }[] | null,
    tone: 'pos' | 'neg',
    empty: string,
    href: (id: string) => string,
  ) =>
    !rows || rows.length === 0 ? (
      <EmptyLine text={empty} />
    ) : (
      <WidgetTable head={[t('dash.colParty'), t('dash.colAmount'), t('dash.colDue'), '']}>
        {rows.map((row) => (
          <tr key={row.documentId} className="border-t">
            <TableCell className="px-0">
              <p className="truncate">{row.party}</p>
              <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.documentNo} /></p>
            </TableCell>
            <TableCell className={cn('px-0 text-end', tone === 'pos' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              <Amount value={row.amount} currency={stated} />
            </TableCell>
            <TableCell className="px-0 text-end text-muted-foreground"><Ltr value={row.due} className="font-mono" /></TableCell>
            <TableCell className="px-0 text-end">
              <Link className="text-primary" to={href(row.documentId)} aria-label={t('dash.openDocument')}>›</Link>
            </TableCell>
          </tr>
        ))}
      </WidgetTable>
    );

  const renderWidget = (id: string) => {
    /**
     * ERR-DSH-04-07 / ERR-DSH-04-08 — DSH-04 §14 row 7 states the sentence
     * verbatim ("Lost the connection mid-load: `This widget could not load.
     * Click refresh to try again.` inside the affected card") and the fenced
     * block restates it as "A widget failed to load. Shown inside that
     * widget's card only."
     *
     * The third and last board to get DSH-02's fix; see
     * `inventory-dashboard-page.tsx` for why the ORDER of these two lines is
     * the fix rather than the message. Without it a failed read never sets
     * `data` and the card shimmers forever.
     *
     * THIS BOARD IS THE ONE WITH TWO QUERIES, and that is why the condition is
     * not a bare `isError`. Everything here reads `data`; **Forecast** and
     * **Projected Balance** additionally read `forecast.data`. So a widget
     * fails when EITHER read it depends on failed:
     *
     *   - the widgets read failed → every card says so, the two forecast cards
     *     included, because without `data` they cannot even state what
     *     currency their figures are in (BR-DSH-04-10);
     *   - only the forecast read failed → ONLY those two say so, and the
     *     eleven cards that never needed it keep their real values.
     *
     * A single `isError || forecast.isError` would have failed all fourteen
     * cards whenever the forecast alone broke, which is the same "inside the
     * affected card only" violation as a page-wide banner, just quieter.
     */
    const needsForecast = id === 'forecast' || id === 'projectedBalance';
    if (isError || (needsForecast && forecast.isError)) {
      return <EmptyLine text={t('dash.widgetFailed')} />;
    }
    if (!data) return <div className="h-16 animate-pulse rounded bg-muted" aria-busy />;
    switch (id) {
      case 'currentCashBalance':
        return (
          <>
            <KpiValue value={data.currentCashBalance} currency={stated} />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('dash.asOf', { time: data.asOf })}
            </p>
          </>
        );
      case 'netCashFlow':
        // BR-DSH-04-08: red only when negative.
        return (
          <>
            <KpiValue value={data.netCashFlow} currency={stated} tone={Number(data.netCashFlow) < 0 ? 'neg' : undefined} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.inMinusOut')}</p>
          </>
        );
      case 'cashIn':
        return (
          <>
            <KpiValue value={data.cashIn} currency={stated} tone="pos" />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.actualReceipts')}</p>
          </>
        );
      case 'cashOut':
        // §14.1.9: routine spending is default ink, never red.
        return (
          <>
            <KpiValue value={data.cashOut} currency={stated} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.actualPayments')}</p>
          </>
        );
      case 'projectedBalance':
        return (
          <>
            <KpiValue
              value={forecast.data?.projectedEnd ?? '0.00'}
              currency={stated}
              tone={forecast.data?.belowZero ? 'neg' : 'pos'}
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.in30Days')}</p>
          </>
        );
      /**
       * THE FORECAST, AND THE TWO ERROR ROWS THAT USED TO ASK FOR AN EMPTY
       * STATE HERE. `ERR-DSH-04-01`'s third clause — "the forecast shows its
       * own empty message" — is the outstanding half of that row; its first
       * two (Current Cash Balance `$0.00`, Cash by Bank Account `No data`)
       * already hold above and in `RankedBars`' own guard.
       *
       * ── WHY THIS IS A NOTE BESIDE A CHART, AND NOT A CHART REPLACED ──────
       *
       * The obvious build — swap the chart for an empty state — DELETES A TRUE
       * STATEMENT. `BR-DSH-04-14` requires the forecast to "start from the
       * CURRENT CASH BALANCE… and walk forward day by day", so when nothing
       * dated falls inside the horizon the flat 31-point line is not an
       * absence of information: it is the answer, and the answer is "your
       * balance stays where it is for a month". Hiding it behind an empty
       * state would tell a solvent company it has nothing to see.
       *
       * `ERR-DSH-04-01` says "its own empty MESSAGE" — "its own" in contrast
       * to a shared one (§14.10 row 13 is a shared string appearing on boards
       * it does not fit), not in contrast to a chart. A dedicated note under a
       * line that still draws satisfies the sentence and keeps the figure.
       *
       * ── THE OTHER ROW'S TRIGGER CANNOT HAPPEN, SO IT IS NOT BUILT ────────
       *
       * §14 row 2 of this screen — `ERR-DSH-04-02` — fires when "no unpaid      rule-coverage:ignore
       * document carries a due date". It never can: `invoices.due_date` and
       * `purchase_invoices.due_date` are both
       * `NOT NULL`, which is how `BR-DSH-04-16` is satisfied — by the schema
       * rather than by a filter, see the note on `DashboardsService.forecast`.
       * A branch built for it would be dead code that looks load-bearing, so
       * there is none. Its REACHABLE cousin is the flat line below, and that
       * is what carries a note.
       *
       * **AND SINCE 27 AUG THE DOCUMENT AGREES: delegated ruling D45 retires
       * that row**, with a notice that states this same schema fact and ends
       * by naming what would revive it — either due-date column becoming
       * nullable. The paragraph above was already true and the rule still sat
       * in the coverage queue, because an ignore mark stops an id counting as
       * CITED and does nothing to the UNNAMED side. Only a notice in the
       * specification could take it out, which is what D45 was for.
       *
       * The mark stays. A retired rule may be cited, so it no longer protects
       * a counter; it protects the claim, because the id appears here inside a
       * sentence explaining why nothing enforces it.
       *
       * ── WHAT EACH NOTE IS ALLOWED TO CLAIM ───────────────────────────────
       *
       * Both conditions are read from figures this screen already has, and
       * both sentences are true of exactly the condition that shows them:
       *
       *   no bank row  → `byBankAccount` is empty, so `currentCashBalance` is
       *     the sum of nothing and day 0 is zero. The note says no bank
       *     balance is INCLUDED rather than that none EXISTS, because the
       *     array is also empty when every account's currency could not be
       *     converted (`ERR-DSH-04-04`) — a sentence about the forecast's
       *     inputs is true in both causes; a sentence about the company is
       *     not.
       *   flat line    → every one of the 31 points carries the same balance,
       *     which is exactly "nothing dated changes this balance". It stays
       *     true in the one case that is not "no documents at all" — a
       *     receipt and a payment of equal size on the same day — because
       *     then the balance genuinely does not change.
       *
       * The string that used to sit here, `Not enough posted history to
       * forecast.`, was wrong for every reachable condition: the forecast
       * reads no history at all, it walks open documents and cheques. It is
       * deleted from both dictionaries rather than reworded, and
       * `cashflow-forecast-note.test.tsx` asserts it stays gone.
       */
      case 'forecast': {
        // Pending, not empty. The two reads are independent, so the widgets
        // query can land first — and the old code showed its (wrong) message
        // in that gap, which is a loading state wearing an answer's clothes.
        if (!forecast.data) return <div className="h-16 animate-pulse rounded bg-muted" aria-busy />;
        const points = forecast.data.points;
        const flat = points.length > 0 && points.every((p) => p.balance === points[0]!.balance);
        return (
          <>
            <div className="mb-1 flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {t('dash.forecastToday')} <Amount value={forecast.data.today} currency={stated} className="text-sm" />
              </span>
              <span className={forecast.data.belowZero ? 'font-semibold text-red-600 dark:text-red-400' : 'font-semibold text-emerald-700 dark:text-emerald-400'}>
                {t('dash.projectedEnd')} <Amount value={forecast.data.projectedEnd} currency={stated} className="text-sm" />
              </span>
            </div>
            <Chart option={forecastOption} height={170} />
            <p className="mt-1 text-end text-xs text-muted-foreground">
              <Ltr value="+30d" className="font-mono" />
            </p>
            {/* GEN-117's informational grey, the same band the disclosure
                uses: this explains a figure, it does not warn about one. */}
            {data.byBankAccount.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">{t('dash.forecastNoBank')}</p>
            ) : null}
            {flat ? <p className="mt-1 text-xs text-muted-foreground">{t('dash.forecastFlat')}</p> : null}
          </>
        );
      }
      case 'cashTrend':
        return data.trend.buckets.length === 0 ? (
          <EmptyLine text={t('dash.noData')} />
        ) : (
          <>
            <p className="mb-1 flex gap-4 text-xs">
              <span className="text-emerald-700 dark:text-emerald-400">
                {t('dash.inflow')} <Amount value={data.trend.inTotal} currency={stated} className="text-xs" />
              </span>
              <span className="text-muted-foreground">
                {t('dash.outflow')} <Amount value={data.trend.outTotal} currency={stated} className="text-xs" />
              </span>
            </p>
            <Chart option={trendOption} height={160} />
          </>
        );
      case 'upcomingWeek':
        /**
         * BR-DSH-04-27 / ERR-DSH-04-06: "the combined Upcoming table must not
         * offer a per-row open action." Four columns and no trailing arrow
         * cell — deliberate, not an omission: §5i says a user who wants the
         * document opens it from one of the two split tables. The absence is
         * the enforcement, so this comment is what stops a later hand adding
         * the arrow the other three tables have.
         */
        return data.upcomingWeek.length === 0 ? (
          <EmptyLine text={t('dash.nothingDueWeek')} />
        ) : (
          <WidgetTable head={[t('dash.colParty'), t('dash.colType'), t('dash.colAmount'), t('dash.colDue')]}>
            {data.upcomingWeek.map((row, index) => (
              <tr key={index} className="border-t">
                <TableCell className="px-0 truncate">{row.party}</TableCell>
                <TableCell className="px-0 text-end text-muted-foreground">{t(`dash.dir_${row.type}`)}</TableCell>
                <TableCell className={cn('px-0 text-end', row.type === 'receivable' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                  <Amount value={row.amount} currency={stated} />
                </TableCell>
                <TableCell className="px-0 text-end text-muted-foreground"><Ltr value={row.due} className="font-mono" /></TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'upcomingReceivables':
        return dueTable(data.upcomingReceivables, 'pos', t('dash.noUpcomingReceivables'), (id) => `/sales/${id}`);
      case 'upcomingPayables':
        return dueTable(data.upcomingPayables, 'neg', t('dash.noUpcomingPayables'), (id) => `/purchases/bills/${id}`);
      case 'overdueReceivables':
        return !data.overdueReceivables || data.overdueReceivables.length === 0 ? (
          <EmptyLine text={t('dash.noOverdueReceivables')} />
        ) : (
          <WidgetTable head={[t('dash.colParty'), t('dash.colAmount'), t('dash.colDaysOverdue'), '']}>
            {data.overdueReceivables.map((row) => (
              <tr key={row.documentId} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.party}</p>
                  <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.documentNo} /></p>
                </TableCell>
                {/* BR-DSH-04-25: severity on the DAYS, amount in default ink. */}
                <TableCell className="px-0 text-end"><Amount value={row.amount} currency={stated} /></TableCell>
                <TableCell className={cn('px-0 text-end', overdueTone(row.daysOverdue))}>
                  <Ltr value={`${row.daysOverdue}d`} className="font-mono" />
                </TableCell>
                <TableCell className="px-0 text-end">
                  <Link className="text-primary" to={`/sales/${row.documentId}`} aria-label={t('dash.openDocument')}>›</Link>
                </TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'checksDue':
        return data.checksDue.length === 0 ? (
          <EmptyLine text={t('dash.noChecksDue')} />
        ) : (
          <WidgetTable head={[t('dash.colCheck'), t('dash.colParty'), t('dash.colAmount'), t('dash.colDue')]}>
            {data.checksDue.map((row) => (
              <tr key={row.checkId} className="border-t">
                {/* BR-DSH-04-29: the cheque's state is COLOURED TEXT in the
                    first column and "must not be drawn as a chip or badge" —
                    a text colour on the cell, shared by both lines, with no
                    rounded/pill wrapper anywhere in this widget. */}
                <TableCell className={cn('px-0', row.state === 'bounced' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400')}>
                  <p className="font-mono"><Ltr value={row.checkNo} /></p>
                  <p className="text-xs">
                    {t(`dash.chk_${row.direction}`)} · {row.state.replace(/_/g, ' ')}
                  </p>
                </TableCell>
                <TableCell className="px-0 text-end">{row.party}</TableCell>
                <TableCell className="px-0 text-end"><Amount value={row.amount} currency={stated} /></TableCell>
                <TableCell className="px-0 text-end text-muted-foreground"><Ltr value={row.due} className="font-mono" /></TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'cashByAccount':
        return (
          <RankedBars
            currency={stated}
            rows={data.byBankAccount.map((r) => ({ name: r.name, value: r.value }))}
          />
        );
      case 'flowCategories':
        return (
          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">{t('dash.inflows')}</p>
            <RankedBars
              tone="pos"
              currency={stated}
              rows={data.categories.inflows.map((c) => ({ name: t(`dash.flow_${c.key}`), value: c.value }))}
            />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('dash.outflows')}</p>
            <RankedBars
              tone="muted"
              currency={stated}
              rows={data.categories.outflows.map((c) => ({ name: t(`dash.flow_${c.key}`), value: c.value }))}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <HomeTabs />
      <h1 className="text-xl font-semibold">{t('dash.tabCashflow')}</h1>
      <Board
        board="cashflow"
        catalog={CASHFLOW_CATALOG}
        stamp={{ text: t('dash.asOf', { time: data?.asOf ?? '' }), refresh: () => void refetch() }}
        /**
         * §6's third control. Consolidated is the empty value, because on the
         * wire the consolidated view is the ABSENCE of the parameter (§9's
         * delivered default) rather than a code meaning "all".
         */
        filters={
          <label className="flex items-center gap-2 text-sm">
            <span className="sr-only">{t('dash.filterCurrency')}</span>
            <select
              className="rounded-md border bg-background px-2 py-1.5"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="">
                {t('dash.currencyConsolidated', { currency: data?.baseCurrency ?? '' })}
              </option>
              {(data?.currencies ?? []).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        }
        /**
         * BR-DSH-04-09 / GEN-116, decided by the SERVER: `rateDate` is non-null
         * exactly when a figure on this board was converted, and null for a
         * single-currency company and for any single-currency filter (§6: "
         * switching to one currency hides it"). So the condition below is the
         * rule, not an approximation of it — the browser is not the one
         * working out whether conversion happened, which is the same reading
         * the CRM-04 statement's `conversionNote` already established.
         *
         * BR-DSH-04-10's sentence, verbatim: "Consolidated to <CUR> · rates as
         * of <YYYY-MM-DD>". Both fields come from the server: the real base
         * currency, and BR-DSH-04-11's RATE date — not the as-of stamp beside
         * it, which is generally a later day.
         *
         * GEN-117: informational grey with the info icon, never amber, never
         * red. ERR-DSH-04-04's notes ride the same band because they are the
         * same fact's other half — what could NOT be converted, and is
         * therefore missing from the figures above.
         */
        disclosure={
          data === undefined ? null : (
            <div className="flex flex-col gap-1">
              {data.rateDate === null ? null : (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  {t('dash.consolidated', {
                    currency: data.baseCurrency,
                    date: data.rateDate,
                  })}
                </p>
              )}
              {data.conversionNotes.map((note) => (
                <p key={note} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  {note}
                </p>
              ))}
            </div>
          )
        }
        renderWidget={renderWidget}
      />
    </div>
  );
}
